import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  FolderKanban,
  Edit2,
  Trash2,
  CheckCircle,
  Clock,
  AlertCircle,
  Plus,
  MoreHorizontal
} from 'lucide-react';

// Mock project data
const mockProjects = {
  1: {
    id: 1,
    name: 'Website Redesign',
    description: 'Complete overhaul of the company website with modern design, improved UX, and better performance. This project includes redesigning all pages, implementing new navigation, and ensuring mobile responsiveness.',
    status: 'active',
    priority: 'high',
    startDate: 'Nov 1, 2024',
    dueDate: 'Dec 15, 2024',
    budget: '$25,000',
    client: 'Internal',
    createdBy: 'John Doe',
    createdAt: 'Oct 15, 2024',
    members: [
      { id: 1, name: 'John Doe', role: 'Project Lead', avatar: 'J' },
      { id: 2, name: 'Sarah Smith', role: 'Designer', avatar: 'S' },
      { id: 3, name: 'Mike Johnson', role: 'Developer', avatar: 'M' },
      { id: 4, name: 'Emily Brown', role: 'QA Engineer', avatar: 'E' },
    ],
    tasks: [
      { id: 1, title: 'Design homepage mockup', status: 'completed', assignee: 'Sarah Smith' },
      { id: 2, title: 'Implement navigation component', status: 'completed', assignee: 'Mike Johnson' },
      { id: 3, title: 'Create responsive layouts', status: 'completed', assignee: 'Mike Johnson' },
      { id: 4, title: 'Design contact page', status: 'in_progress', assignee: 'Sarah Smith' },
      { id: 5, title: 'Implement form validation', status: 'in_progress', assignee: 'Mike Johnson' },
      { id: 6, title: 'Performance optimization', status: 'pending', assignee: 'Mike Johnson' },
      { id: 7, title: 'Cross-browser testing', status: 'pending', assignee: 'Emily Brown' },
      { id: 8, title: 'Final review and launch', status: 'pending', assignee: 'John Doe' },
    ]
  },
  2: {
    id: 2,
    name: 'Mobile App v2',
    description: 'Second version of the mobile application with new features including push notifications, offline mode, and improved performance.',
    status: 'active',
    priority: 'high',
    startDate: 'Oct 15, 2024',
    dueDate: 'Jan 20, 2025',
    budget: '$50,000',
    client: 'External - TechCorp',
    createdBy: 'John Doe',
    createdAt: 'Oct 1, 2024',
    members: [
      { id: 1, name: 'John Doe', role: 'Project Lead', avatar: 'J' },
      { id: 5, name: 'Alex Wilson', role: 'iOS Developer', avatar: 'A' },
      { id: 6, name: 'Lisa Chen', role: 'Android Developer', avatar: 'L' },
    ],
    tasks: [
      { id: 1, title: 'Setup project structure', status: 'completed', assignee: 'Alex Wilson' },
      { id: 2, title: 'Implement authentication', status: 'completed', assignee: 'Lisa Chen' },
      { id: 3, title: 'Build push notifications', status: 'in_progress', assignee: 'Alex Wilson' },
      { id: 4, title: 'Implement offline mode', status: 'pending', assignee: 'Lisa Chen' },
    ]
  }
};

const ProjectDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const project = mockProjects[id] || mockProjects[1];

  const completedTasks = project.tasks.filter(t => t.status === 'completed').length;
  const inProgressTasks = project.tasks.filter(t => t.status === 'in_progress').length;
  const pendingTasks = project.tasks.filter(t => t.status === 'pending').length;
  const progress = Math.round((completedTasks / project.tasks.length) * 100);

  const getStatusBadge = (status) => {
    const statusMap = {
      active: 'aut-badge-active',
      pending: 'aut-badge-pending',
      completed: 'aut-badge-completed',
      archived: 'aut-badge-archived'
    };
    return statusMap[status] || 'aut-badge-active';
  };

  const getTaskStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} style={{ color: 'var(--aut-success)' }} />;
      case 'in_progress':
        return <Clock size={16} style={{ color: 'var(--aut-warning)' }} />;
      default:
        return <AlertCircle size={16} style={{ color: 'var(--aut-text-muted)' }} />;
    }
  };

  const handleDelete = () => {
    // Mock delete action
    navigate('/projects');
  };

  return (
    <div className="aut-project-detail">
      <Link to="/projects" className="aut-back-link">
        <ArrowLeft size={18} />
        <span>Back to Projects</span>
      </Link>

      <div className="aut-detail-header">
        <div className="aut-detail-header-content">
          <div className="aut-detail-icon">
            <FolderKanban size={32} />
          </div>
          <div className="aut-detail-info">
            <h1>{project.name}</h1>
            <p>{project.description}</p>
          </div>
        </div>
        <div className="aut-detail-actions">
          <button className="aut-btn aut-btn-secondary">
            <Edit2 size={18} />
            <span>Edit</span>
          </button>
          <button
            className="aut-btn aut-btn-secondary"
            onClick={() => setShowDeleteConfirm(true)}
            style={{ color: 'var(--aut-error)' }}
          >
            <Trash2 size={18} />
            <span>Delete</span>
          </button>
        </div>
      </div>

      {showDeleteConfirm && (
        <div className="aut-alert aut-alert-error" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Are you sure you want to delete this project? This action cannot be undone.</span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="aut-btn aut-btn-sm aut-btn-secondary" onClick={() => setShowDeleteConfirm(false)}>
              Cancel
            </button>
            <button className="aut-btn aut-btn-sm aut-btn-primary" onClick={handleDelete} style={{ background: 'var(--aut-error)' }}>
              Delete
            </button>
          </div>
        </div>
      )}

      <div className="aut-detail-grid">
        {/* Main Content */}
        <div>
          {/* Progress Section */}
          <div className="aut-detail-section">
            <h3 className="aut-detail-section-title">Progress</h3>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                <span style={{ fontWeight: 500 }}>{progress}% Complete</span>
                <span style={{ color: 'var(--aut-text-muted)' }}>{completedTasks} of {project.tasks.length} tasks</span>
              </div>
              <div className="aut-progress" style={{ height: '8px' }}>
                <div className="aut-progress-bar" style={{ width: `${progress}%` }}></div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '32px' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <CheckCircle size={16} style={{ color: 'var(--aut-success)' }} />
                  <span style={{ fontSize: '24px', fontWeight: 600 }}>{completedTasks}</span>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--aut-text-muted)' }}>Completed</span>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Clock size={16} style={{ color: 'var(--aut-warning)' }} />
                  <span style={{ fontSize: '24px', fontWeight: 600 }}>{inProgressTasks}</span>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--aut-text-muted)' }}>In Progress</span>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <AlertCircle size={16} style={{ color: 'var(--aut-text-muted)' }} />
                  <span style={{ fontSize: '24px', fontWeight: 600 }}>{pendingTasks}</span>
                </div>
                <span style={{ fontSize: '13px', color: 'var(--aut-text-muted)' }}>Pending</span>
              </div>
            </div>
          </div>

          {/* Tasks Section */}
          <div className="aut-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="aut-detail-section-title" style={{ margin: 0 }}>Tasks</h3>
              <button className="aut-btn aut-btn-sm aut-btn-secondary">
                <Plus size={16} />
                <span>Add Task</span>
              </button>
            </div>
            <div className="aut-table-container">
              <table className="aut-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}></th>
                    <th>Task</th>
                    <th>Assignee</th>
                    <th>Status</th>
                    <th style={{ width: '40px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {project.tasks.map((task) => (
                    <tr key={task.id}>
                      <td>{getTaskStatusIcon(task.status)}</td>
                      <td style={{ fontWeight: 500 }}>{task.title}</td>
                      <td>{task.assignee}</td>
                      <td>
                        <span className={`aut-badge ${task.status === 'completed' ? 'aut-badge-completed' : task.status === 'in_progress' ? 'aut-badge-pending' : 'aut-badge-archived'}`}>
                          {task.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </span>
                      </td>
                      <td>
                        <button className="aut-btn aut-btn-ghost aut-btn-icon">
                          <MoreHorizontal size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div>
          {/* Details Section */}
          <div className="aut-detail-section">
            <h3 className="aut-detail-section-title">Details</h3>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Status</div>
              <span className={`aut-badge ${getStatusBadge(project.status)}`}>
                {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
              </span>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Priority</div>
              <div className="aut-detail-value" style={{ textTransform: 'capitalize' }}>{project.priority}</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Start Date</div>
              <div className="aut-detail-value">{project.startDate}</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Due Date</div>
              <div className="aut-detail-value">{project.dueDate}</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Budget</div>
              <div className="aut-detail-value">{project.budget}</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Client</div>
              <div className="aut-detail-value">{project.client}</div>
            </div>
          </div>

          {/* Team Section */}
          <div className="aut-detail-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="aut-detail-section-title" style={{ margin: 0 }}>Team</h3>
              <button className="aut-btn aut-btn-ghost aut-btn-icon">
                <Plus size={16} />
              </button>
            </div>
            {project.members.map((member) => (
              <div key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div className="aut-avatar">{member.avatar}</div>
                <div>
                  <div style={{ fontWeight: 500 }}>{member.name}</div>
                  <div style={{ fontSize: '13px', color: 'var(--aut-text-muted)' }}>{member.role}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Activity Section */}
          <div className="aut-detail-section">
            <h3 className="aut-detail-section-title">Activity</h3>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Created by</div>
              <div className="aut-detail-value">{project.createdBy}</div>
            </div>
            <div className="aut-detail-field">
              <div className="aut-detail-label">Created at</div>
              <div className="aut-detail-value">{project.createdAt}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
